import React, { useState, useMemo } from "react";
import "./Creditbureaureport.scss";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (v, prefix = "₹") => {
  const n = parseFloat(v);
  if (!v || v === "NA" || isNaN(n)) return null;
  return prefix + n.toLocaleString("en-IN");
};

const isNA = (v) => !v || v === "NA" || v === "na" || v === "";

const Val = ({ v }) => (isNA(v) ? <span className="na">—</span> : <>{v}</>);

const fmtAmt = (v) => {
  const n = parseFloat(v);
  if (isNA(v) || isNaN(n)) return "—";
  return "₹" + n.toLocaleString("en-IN");
};

const badgeClass = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("active") || s.includes("open")) return "cbr-badge--active";
  if (s.includes("close") || s.includes("settled")) return "cbr-badge--closed";
  if (s.includes("overdue") || s.includes("npa")) return "cbr-badge--overdue";
  if (s === "na" || s === "—" || !s) return "cbr-badge--default";
  return "cbr-badge--info";
};

// ─── sub-components ──────────────────────────────────────────────────────────
function CollapsibleCard({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cbr-card">
      <div className="cbr-card__header" onClick={() => setOpen((o) => !o)}>
        <h3>{title}</h3>
        <span className={`cbr-card__chevron ${open ? "open" : ""}`}>▼</span>
      </div>
      {open && <div className="cbr-card__body">{children}</div>}
    </div>
  );
}

function KVPairs({ data, exclude = [] }) {
  const entries = Object.entries(data || {}).filter(
    ([k]) => !exclude.includes(k) && typeof data[k] !== "object",
  );
  if (!entries.length) return <p className="cbr-empty">No data available.</p>;
  return (
    <div className="cbr-kv">
      {entries.map(([k, v]) => (
        <div className="cbr-kv__row" key={k}>
          <span className="cbr-kv__key">{camel2Label(k)}</span>
          <span className={`cbr-kv__val ${isNA(v) ? "na" : ""}`}>
            {isNA(v) ? "—" : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function camel2Label(str) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

// ─── Tabs ────────────────────────────────────────────────────────────────────
const TABS = [
  "Overview",
  "Account Summary",
  "Score & NTC",
  "KYC & Identity",
  "Enquiries",
  "Unique Liabilities",
  "Employment",
  "Obligations",
  "Additional Info",
];

// ─── Main Component ──────────────────────────────────────────────────────────
export default function CreditBureauReport({ bureauData , onClose}) {
  const [activeTab, setActiveTab] = useState("Overview");

  console.log("Bureau Data:", bureauData);

  // ── safe destructuring ──────────────────────────────────
  const cbi = bureauData?.data?.creditBureauInfo ?? {};
  const consumer = cbi?.data?.consumer || {};
  const kyc = consumer?.kycInfo || {};
  const accountSummary = consumer?.accountSummary || {};
  console.log("Account Summary:", accountSummary);
  const scoreDetails = consumer?.scoreDetails || {};
  const ntc = consumer?.NTCDetail || {};
  const enquiries = consumer?.enquiries?.enquiry || [];
  const enquirySummary = consumer?.enquirySummary || {};
  const liabilities = consumer?.uniqueLiabilities?.account || [];
  const employment =
    consumer?.employmentInformation?.employmentInformationDetail || [];
  const obligations = consumer?.obligations || {};
  const additionalInfo = consumer?.additionalInformation || {};
  const addresses = kyc?.addresses?.address || [];
  const identifications = kyc?.identifications?.identification || [];
  const emails = kyc?.emails?.email || [];
  const phones = kyc?.telephones?.telephone || kyc?.telephones || [];

  // ─── OVERVIEW tab ─────────────────────────────────────
  const OverviewTab = () => (
    <>
      {/* Score card */}
      <ScoreCard scoreDetails={scoreDetails} ntc={ntc} />

      {/* Account summary metrics */}
      <p className="cbr-section-heading">Account Summary</p>
      <div
        className="cbr-metric-grid"
        style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
      >
        <Metric label="Total Accounts" value={accountSummary.total} />
        <Metric
          label="Active"
          value={accountSummary.activeAccounts}
          variant="success"
        />
        <Metric
          label="Overdue"
          value={accountSummary.overDue}
          variant={accountSummary.overDue > 0 ? "danger" : "success"}
        />
        <Metric label="Zero Balance" value={accountSummary.zeroBalance} />
        <Metric
          label="Current Balance"
          value={fmt(accountSummary.currentBalance)}
        />
        <Metric
          label="Overdue Balance"
          value={fmt(accountSummary.overDueBalance)}
          variant={accountSummary.overDueBalance > 0 ? "warn" : undefined}
        />
        <Metric
          label="High Cr / Sanction"
          value={fmt(accountSummary.highCrOrSancAmt)}
        />
        <Metric label="Account Type" value={accountSummary.accountType} />
      </div>

      {/* Identity snapshot */}
      <p className="cbr-section-heading">Identity Snapshot</p>
      <div className="cbr-two-col">
        <CollapsibleCard title="KYC Summary" defaultOpen>
          <div className="cbr-kv">
            {[
              ["Name", kyc.name],
              ["DOB", kyc.dateOfBirth],
              ["Gender", kyc.gender],
              ["Control No.", kyc.controlNumber],
              ["Member ID", kyc.memberId],
              ["Member Ref No.", kyc.memberReferenceNumber],
            ].map(([k, v]) => (
              <div className="cbr-kv__row" key={k}>
                <span className="cbr-kv__key">{k}</span>
                <span className={`cbr-kv__val ${isNA(v) ? "na" : ""}`}>
                  {isNA(v) ? "—" : v}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleCard>
        <CollapsibleCard title="Transaction IDs" defaultOpen>
          <div className="cbr-kv">
            {[
              ["Bureau Type", cbi.bureauType],
              ["Client Txn ID", cbi.clientTransactionId],
              ["Perfios Txn ID", cbi.perfiosTransactionId],
              ["File Name", cbi.fileName],
              ["Date of Report", consumer.dateOfReport],
            ].map(([k, v]) => (
              <div className="cbr-kv__row" key={k}>
                <span className="cbr-kv__key">{k}</span>
                <span className={`cbr-kv__val ${isNA(v) ? "na" : ""}`}>
                  {isNA(v) ? "—" : v}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleCard>
      </div>
    </>
  );

  // ─── ACCOUNT SUMMARY tab ──────────────────────────────
  const AccountSummaryTab = () => (
    <>
      <p className="cbr-section-heading">Account Summary Details</p>
      <CollapsibleCard title="All Figures" defaultOpen>
        <KVPairs data={accountSummary} />
      </CollapsibleCard>
    </>
  );

  // ─── SCORE & NTC tab ──────────────────────────────────
  const ScoreNTCTab = () => (
    <>
      <ScoreCard scoreDetails={scoreDetails} ntc={ntc} />
      <p className="cbr-section-heading">Score Details</p>
      <CollapsibleCard title="Score Breakdown" defaultOpen>
        <KVPairs data={scoreDetails} />
      </CollapsibleCard>
      <CollapsibleCard title="NTC Detail" defaultOpen>
        <div className="cbr-kv">
          {Object.entries(ntc).map(([k, v]) => (
            <div className="cbr-kv__row" key={k}>
              <span className="cbr-kv__key">{camel2Label(k)}</span>
              <span className={`cbr-kv__val ${isNA(String(v)) ? "na" : ""}`}>
                {isNA(String(v)) ? "—" : String(v)}
              </span>
            </div>
          ))}
        </div>
      </CollapsibleCard>
    </>
  );

  // ─── KYC tab ──────────────────────────────────────────
  const KYCTab = () => (
    <>
      <p className="cbr-section-heading">Identifications</p>
      <div className="cbr-ids" style={{ marginBottom: 20 }}>
        {identifications.length === 0 && (
          <p className="cbr-empty">No IDs found.</p>
        )}
        {identifications.map((id, i) => (
          <div className="cbr-id-pill" key={i}>
            <div className="cbr-id-pill__type">
              {id.identificationType || "—"}
            </div>
            <div className="cbr-id-pill__num">
              {isNA(String(id.idNumber)) ? "—" : String(id.idNumber)}
            </div>
            {!isNA(id.issueDate) && (
              <div style={{ fontSize: 10, color: "#6a9090", marginTop: 2 }}>
                Issued: {id.issueDate}
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="cbr-section-heading">Addresses</p>
      <div style={{ marginBottom: 20 }}>
        {addresses.length === 0 && <p className="cbr-empty">No addresses.</p>}
        {addresses.map((a, i) => (
          <div className="cbr-addr-item" key={i}>
            {[
              a.address1,
              a.address2,
              a.address3,
              a.city,
              a.state,
              a.pinCode,
              a.country,
            ]
              .filter((x) => !isNA(x))
              .join(", ") || "Address details unavailable"}
            <div className="cbr-addr-item__date">
              Reported: {a.dateReported || "—"} · Type: {a.addressType || "—"}
            </div>
          </div>
        ))}
      </div>

      <div className="cbr-two-col">
        <CollapsibleCard title="Email Addresses" defaultOpen>
          {emails.length === 0 && <p className="cbr-empty">No emails.</p>}
          {emails.map((e, i) => (
            <div
              key={i}
              style={{
                padding: "6px 0",
                borderBottom: "1px solid #eef8f7",
                fontSize: 13,
                color: "#3a80c8",
              }}
            >
              {e}
            </div>
          ))}
        </CollapsibleCard>
        <CollapsibleCard title="Phone Numbers" defaultOpen>
          {(!phones || phones.length === 0) && (
            <p className="cbr-empty">No phones.</p>
          )}
          {Array.isArray(phones) &&
            phones.map((p, i) => (
              <div
                key={i}
                style={{
                  padding: "6px 0",
                  borderBottom: "1px solid #eef8f7",
                  fontSize: 13,
                }}
              >
                {typeof p === "object"
                  ? `${p.number || p.telephoneNumber || "—"} (${p.telephoneType || "—"})`
                  : p}
              </div>
            ))}
        </CollapsibleCard>
      </div>
    </>
  );

  // ─── ENQUIRIES tab ────────────────────────────────────
  const EnquiriesTab = () => (
    <>
      <p className="cbr-section-heading">Enquiry Summary</p>
      <CollapsibleCard title="Summary" defaultOpen>
        <KVPairs data={enquirySummary} />
      </CollapsibleCard>
      <p className="cbr-section-heading">Enquiry List ({enquiries.length})</p>
      <div className="cbr-card">
        <div className="cbr-card__body">
          {enquiries.length === 0 && (
            <p className="cbr-empty">No enquiries found.</p>
          )}
          {enquiries.map((enq, i) => (
            <div className="cbr-enq-item" key={i}>
              <div className="cbr-enq-item__left">
                <div className="cbr-enq-item__product">
                  {enq.product || "—"}
                </div>
                <div className="cbr-enq-item__date">
                  {enq.enquiryDate || "—"}
                </div>
                {enq.memberName && (
                  <div className="cbr-enq-item__member">{enq.memberName}</div>
                )}
                {enq.enquiryPurpose && !isNA(enq.enquiryPurpose) && (
                  <span
                    className="cbr-badge cbr-badge--info"
                    style={{ marginTop: 4, display: "inline-block" }}
                  >
                    {enq.enquiryPurpose}
                  </span>
                )}
              </div>
              <div className="cbr-enq-item__amt">{fmtAmt(enq.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // ─── LIABILITIES tab ──────────────────────────────────
  const LiabilitiesTab = () => {
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("");
    const [sortDir, setSortDir] = useState("asc");
    const [typeF, setTypeF] = useState("All");

    const types = useMemo(
      () => [
        "All",
        ...new Set(liabilities.map((a) => a.accountType).filter(Boolean)),
      ],
      [],
    );

    const rows = useMemo(() => {
      const q = search.toLowerCase();
      let data = liabilities.filter((a) => {
        const mt = typeF === "All" || a.accountType === typeF;
        const ms =
          !q ||
          Object.values(a).some((v) => String(v).toLowerCase().includes(q));
        return mt && ms;
      });
      if (sortKey) {
        data = [...data].sort((a, b) => {
          const va = parseFloat(a[sortKey]) || a[sortKey] || "";
          const vb = parseFloat(b[sortKey]) || b[sortKey] || "";
          const na = parseFloat(va),
            nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb))
            return sortDir === "asc" ? na - nb : nb - na;
          return sortDir === "asc"
            ? String(va).localeCompare(String(vb))
            : String(vb).localeCompare(String(va));
        });
      }
      return data;
    }, [search, typeF, sortKey, sortDir]);

    const handleSort = (k) => {
      if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(k);
        setSortDir("asc");
      }
    };

    const totSanction = rows.reduce(
      (s, a) =>
        s + (parseFloat(a.sanctionAmount || a.highCreditOrSanctionAmount) || 0),
      0,
    );
    const totBalance = rows.reduce(
      (s, a) => s + (parseFloat(a.currentBalance) || 0),
      0,
    );
    const totEmi = rows.reduce((s, a) => s + (parseFloat(a.emi) || 0), 0);

    const cols = [
      { key: "accNumber", label: "Acc. No." },
      { key: "accountType", label: "Type" },
      { key: "subscriberName", label: "Lender" },
      { key: "sanctionAmount", label: "Sanction Amt", isAmt: true },
      { key: "currentBalance", label: "Curr. Balance", isAmt: true },
      { key: "emi", label: "EMI", isAmt: true },
      { key: "interestRate", label: "Rate %" },
      { key: "openDate", label: "Open Date" },
      { key: "closedDate", label: "Close Date" },
      { key: "accountStatus", label: "Status" },
    ];

    return (
      <>
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <p
            className="cbr-section-heading"
            style={{ marginBottom: 0, flex: 1 }}
          >
            Unique Liabilities ({rows.length} / {liabilities.length})
          </p>
          {/* <select
            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #afddd8', fontSize: 12, background: '#eef8f7', color: '#1f5c5c' }}
            value={typeF} onChange={(e) => setTypeF(e.target.value)}
          >
            {types.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input
            style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #afddd8', fontSize: 12, background: '#fff', outline: 'none', width: 180 }}
            placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
          /> */}
        </div>
        <div className="cbr-table-wrap">
          <table className="cbr-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} onClick={() => handleSort(c.key)}>
                    {c.label}{" "}
                    {sortKey === c.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={cols.length}
                    className="na"
                    style={{ textAlign: "center", padding: 28 }}
                  >
                    No accounts found.
                  </td>
                </tr>
              )}
              {rows.map((a, i) => (
                <tr key={i}>
                  <td className="mono">{a.accNumber || "—"}</td>
                  <td>{a.accountType || <span className="na">—</span>}</td>
                  <td>
                    {a.subscriberName || a.lender || (
                      <span className="na">—</span>
                    )}
                  </td>
                  <td>
                    {fmtAmt(a.sanctionAmount || a.highCreditOrSanctionAmount)}
                  </td>
                  <td>{fmtAmt(a.currentBalance)}</td>
                  <td>{fmtAmt(a.emi)}</td>
                  <td>
                    {isNA(a.interestRate) ? (
                      <span className="na">—</span>
                    ) : (
                      `${a.interestRate}%`
                    )}
                  </td>
                  <td>{a.openDate || <span className="na">—</span>}</td>
                  <td>{a.closedDate || <span className="na">—</span>}</td>
                  <td>
                    <span
                      className={`cbr-badge ${badgeClass(a.accountStatus || a.status || "")}`}
                    >
                      {a.accountStatus || a.status || "NA"}
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ background: "#d8f0ee", fontWeight: 700 }}>
                  <td
                    colSpan={3}
                    style={{ fontSize: 11, color: "#2a7272", paddingLeft: 14 }}
                  >
                    Totals ({rows.length} accounts)
                  </td>
                  <td style={{ fontWeight: 700, color: "#1f5c5c" }}>
                    {fmtAmt(totSanction)}
                  </td>
                  <td style={{ fontWeight: 700, color: "#1f5c5c" }}>
                    {fmtAmt(totBalance)}
                  </td>
                  <td style={{ fontWeight: 700, color: "#1f5c5c" }}>
                    {fmtAmt(totEmi)}
                  </td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // ─── EMPLOYMENT tab ───────────────────────────────────
  const EmploymentTab = () => (
    <>
      <p className="cbr-section-heading">
        Employment Information ({employment.length})
      </p>
      <div className="cbr-table-wrap">
        <table className="cbr-table">
          <thead>
            <tr>
              <th>Occupation</th>
              <th>Account Type</th>
              <th>Income</th>
              <th>Net/Gross Income</th>
              <th>Date Reported</th>
            </tr>
          </thead>
          <tbody>
            {employment.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="na"
                  style={{ textAlign: "center", padding: 28 }}
                >
                  No employment data.
                </td>
              </tr>
            )}
            {employment.map((e, i) => (
              <tr key={i}>
                <td>{e.occupationCode || <span className="na">—</span>}</td>
                <td>{e.accountType || <span className="na">—</span>}</td>
                <td>{fmtAmt(e.income)}</td>
                <td>
                  {isNA(e.netOrGrossIncome) ? (
                    <span className="na">—</span>
                  ) : (
                    e.netOrGrossIncome
                  )}
                </td>
                <td>{e.dateReported || <span className="na">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  // ─── OBLIGATIONS tab ──────────────────────────────────
  const ObligationsTab = () => (
    <>
      <p className="cbr-section-heading">Obligations</p>
      {typeof obligations === "object" &&
      Object.keys(obligations).length > 0 ? (
        Object.entries(obligations).map(([k, v]) => (
          <CollapsibleCard key={k} title={camel2Label(k)} defaultOpen>
            {Array.isArray(v) ? (
              v.length === 0 ? (
                <p className="cbr-empty">No items.</p>
              ) : (
                <KVPairs data={v[0]} />
              )
            ) : typeof v === "object" && v !== null ? (
              <KVPairs data={v} />
            ) : (
              <p style={{ fontSize: 13 }}>{String(v)}</p>
            )}
          </CollapsibleCard>
        ))
      ) : (
        <p className="cbr-empty">No obligations data.</p>
      )}
    </>
  );

  // ─── ADDITIONAL INFO tab ──────────────────────────────
  const AdditionalInfoTab = () => {
    const accounts = additionalInfo?.accounts?.account || [];
    const rest = { ...additionalInfo };
    delete rest.accounts;
    return (
      <>
        <p className="cbr-section-heading">Additional Information</p>
        <CollapsibleCard title="General Fields" defaultOpen>
          <KVPairs data={rest} />
        </CollapsibleCard>
        {accounts.length > 0 && (
          <CollapsibleCard title={`Accounts (${accounts.length})`} defaultOpen>
            <div className="cbr-table-wrap">
              <table className="cbr-table">
                <thead>
                  <tr>
                    {Object.keys(accounts[0] || {}).map((k) => (
                      <th key={k}>{camel2Label(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a, i) => (
                    <tr key={i}>
                      {Object.values(a).map((v, j) => (
                        <td key={j}>
                          {isNA(String(v)) ? (
                            <span className="na">—</span>
                          ) : (
                            String(v)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleCard>
        )}
      </>
    );
  };

  const tabContent = {
    Overview: <OverviewTab />,
    "Account Summary": <AccountSummaryTab />,
    "Score & NTC": <ScoreNTCTab />,
    "KYC & Identity": <KYCTab />,
    Enquiries: <EnquiriesTab />,
    "Unique Liabilities": <LiabilitiesTab />,
    Employment: <EmploymentTab />,
    Obligations: <ObligationsTab />,
    "Additional Info": <AdditionalInfoTab />,
  };

  return (
    <div className="cbr-root">
      {/* Banner */}
      <div className="cbr-banner">
        <div className="cbr-banner__left">
          <div className="cbr-banner__label">Credit Bureau Report</div>
          <div className="cbr-banner__name">
            {kyc.name || "Consumer Report"}
          </div>
          <div className="cbr-banner__meta">
            <div className="cbr-banner__meta-item">
              DOB: <strong>{kyc.dateOfBirth || "—"}</strong>
            </div>
            <div className="cbr-banner__meta-item">
              Gender: <strong>{kyc.gender || "—"}</strong>
            </div>
            <div className="cbr-banner__meta-item">
              Report Date: <strong>{consumer.dateOfReport || "—"}</strong>
            </div>
          </div>
        </div>
        <div className="" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="cbr-banner__badge cbr-banner__badge--success">
            <span className="dot" /> Response: Success
          </span>
          <span className="cbr-banner__badge cbr-banner__badge--consumer">
            Bureau: {cbi.bureauType || "Consumer"}
          </span>
          <div className="cbr-banner__file">{cbi.fileName}</div>
          {/* <button
            className="cbr-modal__close"
            onClick={() => onClose()}
          >
            ✕
          </button> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="cbr-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`cbr-tabs__tab ${activeTab === t ? "active" : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="cbr-content">{tabContent[activeTab]}</div>

      <div className="cbr-footer">
        CRIF · {cbi.clientTransactionId} · Perfios: {cbi.perfiosTransactionId}
      </div>
    </div>
  );
}

// ─── Shared sub-components ───────────────────────────────────────────────────
function Metric({ label, value, variant }) {
  return (
    <div className={`cbr-metric ${variant ? `cbr-metric--${variant}` : ""}`}>
      <div className="cbr-metric__label">{label}</div>
      <div className="cbr-metric__value">{value ?? "—"}</div>
    </div>
  );
}

function ScoreCard({ scoreDetails, ntc }) {
  const score =
    scoreDetails?.score ||
    scoreDetails?.creditScore ||
    scoreDetails?.bureauScore;
  const scoreName =
    scoreDetails?.scoreName || scoreDetails?.scoreCardName || "Credit Score";
  const scoreVersion =
    scoreDetails?.scoreVersion || scoreDetails?.scoreCardVersion;
  const reasons = scoreDetails?.reasonCodeDetails || scoreDetails?.reasonCodes;
  return (
    <div className="cbr-score-card" style={{ marginBottom: 20 }}>
      <div className="cbr-score-card__circle">
        <span className="cbr-score-card__num">
          {isNA(String(score)) ? "NA" : score}
        </span>
        <span className="cbr-score-card__small">score</span>
      </div>
      <div className="cbr-score-card__info">
        <div className="cbr-score-card__title">{scoreName}</div>
        <div className="cbr-score-card__detail">
          {scoreVersion && `Version: ${scoreVersion}`}
          {scoreVersion && " · "}
          NTC Flag: {String(ntc.NTCFlag ?? "—")} · NTC Score:{" "}
          {ntc.NTCCreditScore || "—"}
          {reasons &&
            ` · ${typeof reasons === "string" ? reasons : JSON.stringify(reasons)}`}
        </div>
      </div>
    </div>
  );
}
